<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="email-change"
      aria-labelledby="email-change-title"
      @cancel.prevent="close"
    >
      <form class="email-change__form" @submit.prevent="confirmChange">
        <header class="email-change__header">
          <h3 id="email-change-title">{{ t(profile.emailAction === 'attach' ? 'emailLink.attach' : 'profile.emailChange') }}</h3>
          <BaseButton
            variant="ghost"
            size="sm"
            type="button"
            :aria-label="t('common.close')"
            :disabled="busy"
            @click="close"
          >
            ×
          </BaseButton>
        </header>
        <p class="email-change__warning">
          {{ t(profile.emailAction === 'attach' ? 'emailLink.siteAccess' : 'profile.emailChangeWarning') }}
        </p>
        <label for="new-email">{{ t('profile.newEmail') }}</label>
        <BaseInput bare
          id="new-email"
          v-model="email"
          class="hm-input"
          type="email"
          required
          :disabled="busy"
          autocomplete="email"
        />
        <label for="new-email-code">{{ t('profile.verificationCode') }}</label>
        <BaseInput bare
          id="new-email-code"
          v-model="code"
          class="hm-input"
          inputmode="numeric"
          autocomplete="one-time-code"
          :disabled="busy"
        />
        <BaseButton
          type="button"
          variant="ghost"
          class="hm-btn"
          :disabled="busy || !email"
          @click="sendCode"
        >
          {{ sending ? t('profile.sending') : t('profile.getCode') }}
        </BaseButton>
        <p v-if="message" role="alert">{{ message }}</p>
        <BaseButton
          type="submit"
          class="hm-btn primary"
          :disabled="busy || !email || !code"
        >
          {{ t(profile.emailAction === 'attach' ? 'emailLink.attach' : 'profile.changeEmail') }}
        </BaseButton>
      </form>
    </dialog>
  </Teleport>
  <SecurityCodeModal
    ref="securityModal"
    :visible="securityVisible"
    action-name="change_email"
    :submitting="saving"
    @close="backToEmail"
    @confirm="save"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import SecurityCodeModal from '@/modules/wallet/components/SecurityCodeModal.vue'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import { parseApiError } from '@/shared/utils/apiErrorParser'
import { useProfileStore } from '../store/profileStore'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ close: []; saved: [] }>()
const { t } = useI18n()
const profile = useProfileStore()
const dialog = ref<HTMLDialogElement | null>(null)
const securityModal = ref<InstanceType<typeof SecurityCodeModal> | null>(null)
const email = ref('')
const code = ref('')
const message = ref('')
const sending = ref(false)
const saving = ref(false)
const securityVisible = ref(false)
const busy = computed(() => sending.value || saving.value)
let active = true
let generation = 0

watch(
  [() => props.visible, () => profile.profile?.id],
  ([visible]) => {
    generation++
    sending.value = false
    saving.value = false
    securityVisible.value = false
    if (visible) {
      email.value = ''
      code.value = ''
      message.value = ''
      dialog.value?.showModal()
    } else {
      dialog.value?.close()
      securityVisible.value = false
    }
  },
  { flush: 'post', immediate: true },
)
onBeforeUnmount(() => {
  active = false
})
onMounted(() => {
  if (props.visible) dialog.value?.showModal()
})

function close(): void {
  if (!busy.value) emit('close')
}

function requestGuard(): () => boolean {
  const attempt = generation
  const id = profile.profile?.id
  return () => active && props.visible && attempt === generation && id === profile.profile?.id
}

function emailError(error: unknown): string {
  const parsed = parseApiError(error)
  return parsed.code === 11012 ? t('emailLink.occupied') : t('profile.emailChangeError')
}

async function sendCode(): Promise<void> {
  if (busy.value || !email.value) return
  const current = requestGuard()
  sending.value = true
  message.value = ''
  try {
    await profile.sendEmailVerificationCode(email.value)
    if (current()) message.value = t('profile.codeSentTo', { email: email.value })
  } catch (error: unknown) {
    if (current()) message.value = emailError(error)
  } finally {
    if (current()) sending.value = false
  }
}

function confirmChange(): void {
  if (busy.value || !email.value || !code.value) return
  dialog.value?.close()
  securityVisible.value = true
}

function backToEmail(): void {
  if (saving.value) return
  securityVisible.value = false
  dialog.value?.showModal()
}

async function save(confirmationCode: string): Promise<void> {
  if (busy.value || !securityVisible.value || !props.visible) return
  const current = requestGuard()
  saving.value = true
  try {
    await profile.changeEmail({
      email: email.value,
      code: code.value,
      confirmAction: 'change_email',
      confirmActionCode: confirmationCode,
    }, current)
    if (!current()) return
    securityVisible.value = false
    emit('saved')
    emit('close')
  } catch (error: unknown) {
    if (!current()) return
    if (securityModal.value?.showCodeError(error)) return
    const parsed = parseApiError(error)
    if (parsed.fieldErrors.confirmActionCode) {
      securityModal.value?.showError(error)
      return
    }
    securityModal.value?.clearCode()
    securityVisible.value = false
    message.value = emailError(error)
    dialog.value?.showModal()
  } finally {
    if (current()) saving.value = false
  }
}
</script>

<style scoped lang="scss">
.email-change {
  margin: auto;
  width: min(420px, calc(100% - 32px));
  max-height: 90dvh;
  padding: 24px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card-lg);
  background: var(--ds-surface);
  color: var(--ds-text);
}
.email-change::backdrop {
  background: var(--ds-scrim);
}
.email-change__form {
  display: grid;
  gap: 12px;
}
.email-change__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.email-change__warning {
  padding: 12px;
  background: var(--ds-warning-soft);
  border-radius: var(--ds-radius-control);
}

.hm-input {
  width: 100%;
  box-sizing: border-box;
  padding: var(--p-space-3) var(--p-space-4);
  font: inherit;
  color: var(--ds-text);
  background: var(--ds-input-bg);
  border: 1px solid var(--ds-input-border);
  border-radius: var(--ds-input-radius);
}
.hm-input:focus-visible {
  outline: none;
  box-shadow: var(--ds-focus-ring-shadow);
}
</style>
